import { useState } from "react";
import { 
  Package,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  ShoppingCart,
  Truck,
  BarChart3,
  Search,
  Filter,
  Download,
  Upload,
  Settings,
  Bell,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Calendar,
  Target,
  Zap,
  Archive,
  RefreshCw,
  Plus,
  Minus,
  Edit,
  Eye,
  Building
} from "lucide-react";

// 基本的なUIコンポーネント
const Button = ({ children, variant = "default", size = "default", className = "", disabled = false, onClick, ...props }) => {
  const baseClasses = "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";
  
  const variants = {
    default: "bg-blue-600 text-white hover:bg-blue-700",
    outline: "border border-gray-300 bg-white hover:bg-gray-50",
    ghost: "hover:bg-gray-100"
  };
  
  const sizes = {
    default: "h-10 py-2 px-4",
    sm: "h-9 px-3 text-sm"
  };
  
  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg border shadow-sm ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children, className = "" }) => (
  <div className={`p-6 pb-0 ${className}`}>
    {children}
  </div>
);

const CardContent = ({ children, className = "" }) => (
  <div className={`p-6 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children, className = "" }) => (
  <h3 className={`text-lg font-semibold leading-none tracking-tight ${className}`}>
    {children}
  </h3>
);

const Badge = ({ children, variant = "default", className = "" }) => {
  const variants = {
    default: "bg-blue-100 text-blue-800",
    outline: "border border-gray-300 bg-white text-gray-700",
    secondary: "bg-gray-100 text-gray-800"
  };
  
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${variants[variant]} ${className}`}>
      {children}
    </span>
  );
};

const Input = ({ placeholder, value, onChange, className = "", ...props }) => (
  <input
    type="text"
    className={`flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    {...props}
  />
);

const Progress = ({ value, className = "" }) => {
  const percentage = Math.min(Math.max(value, 0), 100);
  
  return (
    <div className={`relative h-4 w-full overflow-hidden rounded-full bg-gray-200 ${className}`}>
      <div 
        className="h-full w-full flex-1 bg-blue-600 transition-all"
        style={{ transform: `translateX(-${100 - percentage}%)` }}
      />
    </div>
  );
};

export default function InventoryOrderingSystem() {
  const [activeTab, setActiveTab] = useState("inventory");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  // サンプル在庫データ
  const inventoryItems = [
    {
      id: "inv_1",
      itemCode: "MB-W611",
      name: "マイクロバイポーラ鉗子（ストレート）",
      nicknames: ["ヤキ", "赤バイ", "バイポ"],
      category: "電気メス用器械",
      currentStock: 5,
      reorderLevel: 10,
      maxStock: 25,
      unitCost: 1250,
      totalValue: 6250,
      supplier: "オリンパス",
      location: "倉庫A-棚3",
      lastUpdated: "2025-06-07",
      usageRate: 15,
      leadTime: 7,
      status: "要発注",
      expiryDate: null,
      batchNumber: "B2025001"
    },
    {
      id: "inv_2",
      itemCode: "J491H",
      name: "縫合糸（3-0バイクリル）",
      nicknames: ["3-0", "バイクリル"],
      category: "縫合材料",
      currentStock: 120,
      reorderLevel: 50,
      maxStock: 200,
      unitCost: 280,
      totalValue: 33600,
      supplier: "ジョンソン・エンド・ジョンソン",
      location: "症例カート",
      lastUpdated: "2025-06-06",
      usageRate: 45,
      leadTime: 14,
      status: "適正",
      expiryDate: "2027-12-31",
      batchNumber: "JJ2025043"
    },
    {
      id: "inv_3",
      itemCode: "ECPS12",
      name: "腹腔鏡用トロッカー（12mm）",
      nicknames: ["トロッカー", "ポート", "12mm"],
      category: "腹腔鏡器械",
      currentStock: 25,
      reorderLevel: 20,
      maxStock: 50,
      unitCost: 850,
      totalValue: 21250,
      supplier: "エチコン",
      location: "倉庫B-棚1",
      lastUpdated: "2025-06-05",
      usageRate: 12,
      leadTime: 10,
      status: "適正",
      expiryDate: "2026-08-15",
      batchNumber: "EC2025089"
    },
    {
      id: "inv_4",
      itemCode: "TI-2406",
      name: "チタンプレート（2.4mm、6穴）",
      nicknames: ["プレート", "チタンプレ"],
      category: "インプラント",
      currentStock: 2,
      reorderLevel: 3,
      maxStock: 10,
      unitCost: 45000,
      totalValue: 90000,
      supplier: "ストライカー",
      location: "医在庫室",
      lastUpdated: "2025-06-04",
      usageRate: 5,
      leadTime: 21,
      status: "緊急発注",
      expiryDate: null,
      batchNumber: "STR2025012"
    }
  ];

  // 発注履歴データ
  const orderHistory = [
    {
      id: "order_1",
      orderNumber: "PO-2025-001",
      supplier: "オリンパス",
      orderDate: "2025-06-01",
      expectedDelivery: "2025-06-08",
      actualDelivery: "2025-06-07",
      status: "完了",
      totalAmount: 125000,
      items: [
        { name: "マイクロバイポーラ鉗子", quantity: 100, unitCost: 1250 }
      ]
    },
    {
      id: "order_2",
      orderNumber: "PO-2025-002",
      supplier: "ジョンソン・エンド・ジョンソン",
      orderDate: "2025-06-03",
      expectedDelivery: "2025-06-17",
      actualDelivery: null,
      status: "配送中",
      totalAmount: 84000,
      items: [
        { name: "縫合糸（3-0バイクリル）", quantity: 300, unitCost: 280 }
      ]
    },
    {
      id: "order_3",
      orderNumber: "PO-2025-003",
      supplier: "ストライカー",
      orderDate: "2025-06-06",
      expectedDelivery: "2025-06-27",
      actualDelivery: null,
      status: "承認待ち",
      totalAmount: 450000,
      items: [
        { name: "チタンプレート（2.4mm、6穴）", quantity: 10, unitCost: 45000 }
      ]
    }
  ];

  // サプライヤーデータ
  const suppliers = [
    {
      id: "sup_1",
      name: "オリンパス",
      contact: "03-1234-5678",
      email: "orders@olympus.co.jp",
      leadTime: 7,
      reliability: 98,
      categories: ["電気メス用器械", "内視鏡器械"],
      paymentTerms: "月末締め翌月末払い",
      deliveryOptions: ["通常配送", "緊急配送"],
      minimumOrder: 50000
    },
    {
      id: "sup_2",
      name: "ジョンソン・エンド・ジョンソン",
      contact: "03-2345-6789",
      email: "orders@jj.co.jp",
      leadTime: 14,
      reliability: 95,
      categories: ["縫合材料", "消毒薬"],
      paymentTerms: "月末締め翌月末払い",
      deliveryOptions: ["通常配送"],
      minimumOrder: 30000
    },
    {
      id: "sup_3",
      name: "ストライカー",
      contact: "03-3456-7890",
      email: "orders@stryker.co.jp",
      leadTime: 21,
      reliability: 92,
      categories: ["インプラント", "手術器械"],
      paymentTerms: "月末締め翌々月末払い",
      deliveryOptions: ["通常配送", "緊急配送"],
      minimumOrder: 100000
    }
  ];

  const getStockStatus = (item) => {
    const stockRatio = item.currentStock / item.reorderLevel;
    if (stockRatio <= 0.5) return { status: "緊急発注", color: "bg-red-100 text-red-800", level: "danger" };
    if (stockRatio <= 1) return { status: "要発注", color: "bg-yellow-100 text-yellow-800", level: "warning" };
    if (stockRatio <= 2) return { status: "適正", color: "bg-green-100 text-green-800", level: "good" };
    return { status: "過剰", color: "bg-blue-100 text-blue-800", level: "overstocked" };
  };

  const getOrderStatus = (status) => {
    const colors = {
      "承認待ち": "bg-yellow-100 text-yellow-800",
      "発注済み": "bg-blue-100 text-blue-800",
      "配送中": "bg-purple-100 text-purple-800",
      "完了": "bg-green-100 text-green-800",
      "キャンセル": "bg-red-100 text-red-800"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const filteredItems = inventoryItems.filter(item => {
    const matchesSearch = 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.nicknames.some(nickname => nickname.toLowerCase().includes(searchTerm.toLowerCase())) ||
      item.itemCode.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = filterCategory === "all" || item.category === filterCategory;
    const matchesStatus = filterStatus === "all" || getStockStatus(item).status === filterStatus;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const renderInventoryView = () => (
    <div className="space-y-6">
      {/* 在庫概要 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-sm text-gray-600">総アイテム数</p>
                <p className="text-2xl font-bold">{inventoryItems.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-sm text-gray-600">要発注</p>
                <p className="text-2xl font-bold text-red-600">
                  {inventoryItems.filter(item => getStockStatus(item).level === "warning" || getStockStatus(item).level === "danger").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">総在庫価値</p>
                <p className="text-2xl font-bold">
                  ¥{inventoryItems.reduce((sum, item) => sum + item.totalValue, 0).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-sm text-gray-600">月間使用額</p>
                <p className="text-2xl font-bold">¥2.1M</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 検索・フィルタ */}
      <div className="flex gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="アイテム名・通称・コードで検索..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="all">全カテゴリ</option>
          <option value="電気メス用器械">電気メス用器械</option>
          <option value="縫合材料">縫合材料</option>
          <option value="腹腔鏡器械">腹腔鏡器械</option>
          <option value="インプラント">インプラント</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="all">全ステータス</option>
          <option value="緊急発注">緊急発注</option>
          <option value="要発注">要発注</option>
          <option value="適正">適正</option>
          <option value="過剰">過剰</option>
        </select>
      </div>

      {/* アラート */}
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            <div className="flex-1">
              <h3 className="font-semibold text-red-800">緊急発注が必要なアイテム</h3>
              <p className="text-sm text-red-700">
                チタンプレート（2個）、マイクロバイポーラ鉗子（5個）の在庫が緊急レベルまで減少しています。
              </p>
            </div>
            <Button className="bg-red-600 hover:bg-red-700">
              一括発注
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 在庫一覧 */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>在庫一覧</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                CSV出力
              </Button>
              <Button variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                在庫更新
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredItems.map(item => {
              const stockStatus = getStockStatus(item);
              const stockPercentage = (item.currentStock / item.maxStock) * 100;
              
              return (
                <div key={item.id} className="border rounded p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold">{item.name}</h4>
                        <Badge className={stockStatus.color}>
                          {stockStatus.status}
                        </Badge>
                        <Badge variant="outline">{item.category}</Badge>
                      </div>

                      {/* 通称表示 */}
                      {item.nicknames.length > 0 && (
                        <div className="mb-2">
                          <span className="text-sm text-gray-600">通称: </span>
                          {item.nicknames.map((nickname, index) => (
                            <Badge key={index} variant="secondary" className="mr-1 text-xs">
                              {nickname}
                            </Badge>
                          ))}
                        </div>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 text-sm mb-3">
                        <div>
                          <span className="text-gray-600">コード:</span> {item.itemCode}
                        </div>
                        <div>
                          <span className="text-gray-600">現在庫:</span> {item.currentStock}
                        </div>
                        <div>
                          <span className="text-gray-600">発注点:</span> {item.reorderLevel}
                        </div>
                        <div>
                          <span className="text-gray-600">単価:</span> ¥{item.unitCost.toLocaleString()}
                        </div>
                        <div>
                          <span className="text-gray-600">保管場所:</span> {item.location}
                        </div>
                        <div>
                          <span className="text-gray-600">サプライヤー:</span> {item.supplier}
                        </div>
                      </div>

                      {/* 在庫レベル表示 */}
                      <div className="mb-3">
                        <div className="flex justify-between text-sm mb-1">
                          <span>在庫レベル</span>
                          <span>{item.currentStock}/{item.maxStock}</span>
                        </div>
                        <Progress value={stockPercentage} className="h-2" />
                      </div>

                      <div className="flex gap-4 text-xs text-gray-500">
                        <span>月間使用: {item.usageRate}個</span>
                        <span>リードタイム: {item.leadTime}日</span>
                        {item.expiryDate && (
                          <span>有効期限: {item.expiryDate}</span>
                        )}
                        <span>バッチ: {item.batchNumber}</span>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 ml-4">
                      <Button size="sm" disabled={stockStatus.level === "good"}>
                        <ShoppingCart className="w-4 h-4 mr-1" />
                        発注
                      </Button>
                      <Button variant="outline" size="sm">
                        <Edit className="w-4 h-4 mr-1" />
                        編集
                      </Button>
                      <Button variant="outline" size="sm">
                        <Eye className="w-4 h-4 mr-1" />
                        履歴
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderOrdersView = () => (
    <div className="space-y-6">
      {/* 発注概要 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-yellow-600" />
              <div>
                <p className="text-sm text-gray-600">承認待ち</p>
                <p className="text-2xl font-bold">
                  {orderHistory.filter(order => order.status === "承認待ち").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Truck className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-sm text-gray-600">配送中</p>
                <p className="text-2xl font-bold">
                  {orderHistory.filter(order => order.status === "配送中").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">今月完了</p>
                <p className="text-2xl font-bold">
                  {orderHistory.filter(order => order.status === "完了").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-sm text-gray-600">今月発注額</p>
                <p className="text-2xl font-bold">
                  ¥{orderHistory.reduce((sum, order) => sum + order.totalAmount, 0).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* クイック発注 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            クイック発注
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button className="h-20 flex-col">
              <AlertTriangle className="w-6 h-6 mb-2" />
              緊急発注を実行
            </Button>
            <Button variant="outline" className="h-20 flex-col">
              <RefreshCw className="w-6 h-6 mb-2" />
              定期発注を実行
            </Button>
            <Button variant="outline" className="h-20 flex-col">
              <Target className="w-6 h-6 mb-2" />
              カスタム発注
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 発注履歴 */}
      <Card>
        <CardHeader>
          <CardTitle>発注履歴</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {orderHistory.map(order => (
              <div key={order.id} className="border rounded p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-semibold">{order.orderNumber}</h4>
                      <Badge className={getOrderStatus(order.status)}>
                        {order.status}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                      <div>
                        <span className="text-gray-600">サプライヤー:</span> {order.supplier}
                      </div>
                      <div>
                        <span className="text-gray-600">発注日:</span> {order.orderDate}
                      </div>
                      <div>
                        <span className="text-gray-600">予定日:</span> {order.expectedDelivery}
                      </div>
                      <div>
                        <span className="text-gray-600">金額:</span> ¥{order.totalAmount.toLocaleString()}
                      </div>
                    </div>

                    <div className="mb-2">
                      <span className="text-sm text-gray-600">発注アイテム:</span>
                      <div className="mt-1">
                        {order.items.map((item, index) => (
                          <div key={index} className="text-sm">
                            • {item.name} × {item.quantity} (¥{item.unitCost.toLocaleString()}/個)
                          </div>
                        ))}
                      </div>
                    </div>

                    {order.actualDelivery && (
                      <div className="text-sm text-green-600">
                        実際の納期: {order.actualDelivery}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    <Button variant="outline" size="sm">
                      詳細
                    </Button>
                    {order.status === "承認待ち" && (
                      <>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700">
                          承認
                        </Button>
                        <Button variant="outline" size="sm">
                          却下
                        </Button>
                      </>
                    )}
                    {order.status === "配送中" && (
                      <Button variant="outline" size="sm">
                        追跡
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderSuppliersView = () => (
    <div className="space-y-6">
      {/* サプライヤー概要 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Building className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-sm text-gray-600">登録サプライヤー</p>
                <p className="text-2xl font-bold">{suppliers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">平均信頼度</p>
                <p className="text-2xl font-bold">
                  {Math.round(suppliers.reduce((sum, sup) => sum + sup.reliability, 0) / suppliers.length)}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-sm text-gray-600">平均リードタイム</p>
                <p className="text-2xl font-bold">
                  {Math.round(suppliers.reduce((sum, sup) => sum + sup.leadTime, 0) / suppliers.length)}日
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* サプライヤー一覧 */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>サプライヤー一覧</CardTitle>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              新規登録
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {suppliers.map(supplier => (
              <div key={supplier.id} className="border rounded p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-semibold text-lg">{supplier.name}</h4>
                      <Badge className={
                        supplier.reliability >= 95 ? "bg-green-100 text-green-800" :
                        supplier.reliability >= 90 ? "bg-yellow-100 text-yellow-800" :
                        "bg-red-100 text-red-800"
                      }>
                        信頼度 {supplier.reliability}%
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mb-3">
                      <div>
                        <span className="text-gray-600">連絡先:</span> {supplier.contact}
                      </div>
                      <div>
                        <span className="text-gray-600">Email:</span> {supplier.email}
                      </div>
                      <div>
                        <span className="text-gray-600">リードタイム:</span> {supplier.leadTime}日
                      </div>
                      <div>
                        <span className="text-gray-600">支払条件:</span> {supplier.paymentTerms}
                      </div>
                      <div>
                        <span className="text-gray-600">最小発注額:</span> ¥{supplier.minimumOrder.toLocaleString()}
                      </div>
                      <div>
                        <span className="text-gray-600">配送オプション:</span> {supplier.deliveryOptions.join(", ")}
                      </div>
                    </div>

                    <div className="mb-2">
                      <span className="text-sm text-gray-600">取扱カテゴリ:</span>
                      <div className="flex gap-1 mt-1">
                        {supplier.categories.map(category => (
                          <Badge key={category} variant="outline" className="text-xs">
                            {category}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 ml-4">
                    <Button variant="outline" size="sm">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm">
                      発注
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="w-full max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">在庫管理・発注システム</h1>
        <div className="flex gap-2">
          <Button variant="outline">
            <Bell className="w-4 h-4 mr-2" />
            アラート設定
          </Button>
          <Button variant="outline">
            <Settings className="w-4 h-4 mr-2" />
            システム設定
          </Button>
        </div>
      </div>

      {/* タブナビゲーション */}
      <div className="flex gap-2 border-b">
        <Button 
          variant={activeTab === "inventory" ? "default" : "ghost"}
          onClick={() => setActiveTab("inventory")}
        >
          <Package className="w-4 h-4 mr-2" />
          在庫管理
        </Button>
        <Button 
          variant={activeTab === "orders" ? "default" : "ghost"}
          onClick={() => setActiveTab("orders")}
        >
          <ShoppingCart className="w-4 h-4 mr-2" />
          発注管理
        </Button>
        <Button 
          variant={activeTab === "suppliers" ? "default" : "ghost"}
          onClick={() => setActiveTab("suppliers")}
        >
          <Building className="w-4 h-4 mr-2" />
          サプライヤー
        </Button>
        <Button 
          variant={activeTab === "analytics" ? "default" : "ghost"}
          onClick={() => setActiveTab("analytics")}
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          分析
        </Button>
      </div>

      {/* コンテンツ */}
      {activeTab === "inventory" && renderInventoryView()}
      {activeTab === "orders" && renderOrdersView()}
      {activeTab === "suppliers" && renderSuppliersView()}
      {activeTab === "analytics" && (
        <div className="text-center py-12">
          <h3 className="text-lg font-semibold mb-2">在庫分析機能</h3>
          <p className="text-gray-600 mb-4">在庫回転率、ABC分析、トレンド分析などの詳細レポート</p>
          <Button variant="outline">準備中</Button>
        </div>
      )}
    </div>
  );
}